/* istanbul ignore file */
/**
 * Created by rathompson on 6/22/22
 * File: DomainInfoForm
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useContext, EventHandler, Dispatch, SetStateAction, useEffect } from 'react'
import { Bold, Button, ModalFooter, Title } from '@panwds/react-ui'
import {
  TITLE_DOMAIN_INFO,
  LABEL_DOMAIN,
  BUTTON_NEXT,
  BUTTON_CANCEL,
  PLACEHOLDER_DOMAIN_INFORMATION,
  ERROR_INVALID_DOMAIN,
  DOMAIN_MAX_LENGTH,
  ERROR_INVALID_DOMAIN_LENGTH,
  ERROR_INVALID_USED_DOMAIN,
} from '../../../constants/LabelConstants'
import isFQDN from 'validator/lib/isFQDN'
import { Form, Input, SubmitButton } from '@panwds/react-form'
import { DATA_TEST_DOMAIN_CANCEL_BUTTON, DATA_TEST_DOMAIN_INPUT, DATA_TEST_DOMAIN_SUBMIT_BUTTON } from '../../../../common/constants/TestingDataIds'
import GlobalContext from '../../../../common/context/GlobalContext'
import { IDP_OWNER_TYPES } from '../../../constants/IAMConstants'
import { CreateFederationOwner } from '../../../actions/IAMActions'
import { useFormContext } from 'react-hook-form'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'

const DomainInfoForm: VFC<{
  setLoading: Dispatch<SetStateAction<boolean>>,
  loading: boolean,
  onBackPressed: EventHandler<any>,
  onNextPressed: Function,
  setDomain: Dispatch<SetStateAction<string>>,
  domain: string,
  setVerificationString: Dispatch<SetStateAction<string>>,
  verificationString: string,
  email: string,
  federations: any,
  savedDomain?: string
}> = ({ setLoading, loading, onBackPressed, onNextPressed, setDomain, domain, setVerificationString, email, verificationString, federations, savedDomain }) => {
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  const onSubmit = useCallback(async () => {
    const tsg_id = selectedTreeNode?.id
    if (verificationString) {
      onNextPressed()
      return
    }

    if (tsg_id) {
      setLoading(true)

      try {
        const { data: { verification: { verificationString } } } = await CreateFederationOwner(tsg_id, domain, {
          user: email,
          verification: { type: IDP_OWNER_TYPES.DNS_TEXT }
        })
        setVerificationString(verificationString)
        setLoading(false)
        onNextPressed()
      }
      catch (error) {
        setLoading(false)
      }
    }
    setLoading(false)
  }, [selectedTreeNode?.id, verificationString, setLoading, onNextPressed, domain, email, setVerificationString])

  return (
    <Form onSubmit={onSubmit} mode='onBlur'>
      <FormContent {...{ setDomain, domain, loading, onBackPressed, onSubmit, verificationString, federations, savedDomain }} />
    </Form>
  )
}

const FormContent: VFC<{
  setDomain: Dispatch<SetStateAction<string>>,
  domain: string,
  loading: boolean,
  onBackPressed: EventHandler<any>,
  verificationString: string,
  federations: any,
  savedDomain?: string
}> = ({ setDomain, domain, loading, onBackPressed, verificationString, federations, savedDomain }) => {
  const { trigger, formState: { isValid } } = useFormContext()
  const domains = federations?.items?.map((f: any) => f?.domain)

  useEffect(() => {
    savedDomain && setDomain(savedDomain)
  }, [setDomain, savedDomain])

  return (
    <>
      <div className='tw-absolute tw-inset-0 tw-overflow-auto' style={{ bottom: '43px' }}>
        {loading && <ScreenLoader appearance='light'/>}
        <div className='tw-flex tw-flex-col tw-gap-1 tw-p-4' style={{ width: '80%' }}>
          <Title level={1} size='lg' addClassName='tw-font-semibold'>{TITLE_DOMAIN_INFO}</Title>
          <div className='tw-flex tw-flex-col tw-mt-4 tw-gap-2'>
            <Bold>{LABEL_DOMAIN}</Bold>
            <Input
              onInput={(event) => {
                setDomain(event?.currentTarget?.value)
              }}
              name={LABEL_DOMAIN}
              placeholder={PLACEHOLDER_DOMAIN_INFORMATION}
              label={LABEL_DOMAIN}
              showLabel={false}
              value={domain}
              data-testid={DATA_TEST_DOMAIN_INPUT}
              register={{
                validate: {
                  isValidDomain: value => isFQDN(value) || ERROR_INVALID_DOMAIN,
                  isCorrectLength: value => value.length <= DOMAIN_MAX_LENGTH || ERROR_INVALID_DOMAIN_LENGTH,
                  isInFederations: value => !domains?.includes(value) || ERROR_INVALID_USED_DOMAIN
                },
                setValueAs: value => value.trimEnd(),
                onChange: () => trigger && trigger(LABEL_DOMAIN),
                required: true
              }}
            />
          </div>
        </div>
      </div>

      <ModalFooter addClassName='tw-absolute' style={{ right: 0, left: 0, bottom: 0, height: '43px' }}>
        <Button
          onClick={onBackPressed}
          appearance="secondary"
          disabled={loading}
          dataTestId={DATA_TEST_DOMAIN_CANCEL_BUTTON}
        >{BUTTON_CANCEL}</Button>
        <SubmitButton disabled={loading || !isValid} isLoading={loading} data-testid={DATA_TEST_DOMAIN_SUBMIT_BUTTON}>
          {BUTTON_NEXT}
        </SubmitButton>
      </ModalFooter>
    </>
  )
}

export default DomainInfoForm

